/**
 * Turn the several address shapes stored by old and new checkout flows into
 * render-safe text. React cannot render an object such as { address: "..." }
 * directly; doing so blanks the whole production page with invariant #31.
 */
export const getAddressText = (value, fallback = "—", depth = 0) => {
  if (typeof value === "string") return value.trim() || fallback;
  if (typeof value === "number") return String(value);
  if (!value || typeof value !== "object" || depth > 3) return fallback;

  const keys = [
    "address",
    "formattedAddress",
    "formatted_address",
    "name",
    "vicinity",
    "description",
  ];
  for (const key of keys) {
    if (value[key] === value) continue;
    const text = getAddressText(value[key], "", depth + 1);
    if (text) return text;
  }
  return fallback;
};

export const getShippingAddressText = (shippingAddress, fallback = "—") => {
  if (!shippingAddress) return fallback;
  return getAddressText(
    shippingAddress.address,
    getAddressText(shippingAddress.locationName, fallback)
  );
};

const idText = (value) => {
  const candidate = value && typeof value === "object"
    ? value._id || value.id
    : value;
  if (candidate == null) return null;
  const text = String(candidate).trim();
  return text && text !== "[object Object]" ? text : null;
};

/** Resolve the vendor who owns this order, not merely the logged-in operator. */
export const getOrderVendorId = (order, fallback = null) => {
  const direct = [order?.vendorId, order?.restaurantVendorId, order?.sellerId];
  for (const candidate of direct) {
    const id = idText(candidate);
    if (id) return id;
  }

  for (const item of order?.orderItems || []) {
    const id = idText(item?.vendorId);
    if (id) return id;
  }

  for (const vendorOrder of order?.vendorOrders || []) {
    const id = idText(vendorOrder?.vendorId);
    if (id) return id;
  }

  return idText(fallback);
};

/* The payment rail as a customer or finance reader would name it. The order
   stores the client's enum (MOMO, Airtel, CARD, COD, ...); a detail page that
   prints "MOMO" is showing a database value, not a method. Unknown values
   pass through unchanged so a new rail is never hidden. */
const PAYMENT_METHOD_LABEL = {
  MOMO: "MTN Mobile Money",
  MTN: "MTN Mobile Money",
  AIRTEL: "Airtel Money",
  CARD: "Card",
  COD: "Cash on delivery",
  CASH: "Cash",
  ZAMTEL: "Zamtel Kwacha",
};
export const getPaymentMethodLabel = (method, fallback = "Not recorded") => {
  if (!method) return fallback;
  const key = String(method).trim().toUpperCase();
  return PAYMENT_METHOD_LABEL[key] || String(method);
};

export default { getAddressText, getShippingAddressText, getOrderVendorId, getPaymentMethodLabel };
